import React, { useContext, useState } from "react";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

import styles from "./index.module.css";
import TimerBadge from "../../../components/TimerBadge";
import ExitButton from "../Buttons/ExitButton";
import ParticipantBadge from "../../../components/ParticipantBadge";
import participantIcon from "../../../assets/images/user_purple.svg";
import clockIcon from "../../../assets/images/clockIcon.svg";
import exitIcon from "../../../assets/images/exit.svg";
import ExitDialog from "./ExitDialog";
import { Button } from "../../../components/ui/Button";
import { HelpCircle } from "lucide-react";
import FeedbackDialog from "../../../pages/FeedbackPage/FeedbackDialog";

import { InterviewContext } from "../../../contexts/InterviewContext";
import { useAuth } from "src/contexts/AuthContext";
import { FEEDBACK_FORM_TITLES } from "../../../pages/FeedbackPage/types";

interface MockCaseHeaderProps {
    title?: string;
    participantCount?: number;
}

const MockCaseHeader: React.FC<MockCaseHeaderProps> = ({
    title,
    participantCount = 2,
}) => {
    const { firebaseUser } = useAuth();
    const navigate = usePartnerAwareNavigate();
    const { quitInterviewHandler } = useContext(InterviewContext)!;
    const [showExitDialog, setShowExitDialog] = useState(false);
    const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

    const handleExit = async () => {
        setShowExitDialog(true);
    };

    const handleStillLeave = async () => {
        try {
            await quitInterviewHandler();
            navigate("/");
            // Refresh the page after navigation is initiated
            window.location.reload();
        } catch (err) {
            console.error("Error stopping interview:", err);
            // Still try to navigate home if there's an error
            navigate("/");
        }
    };

    const handleStayToContinue = () => {
        setShowExitDialog(false);
    };

    const handleTechSupport = () => {
        setShowFeedbackDialog(true);
    };

    return (
        <>
            <div className={styles.headerContainer}>
                {/* First row: Title (left), Time + Exit (right) */}
                <div className={styles.topRow}>
                    <div className={styles.leftSection}>
                        <h2 className={styles.title}>{title}</h2>
                        <ParticipantBadge
                            count={participantCount}
                            iconSrc={participantIcon}
                        />
                    </div>
                    <div className={styles.rightSection}>
                        <TimerBadge iconSrc={clockIcon} />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleTechSupport}
                            className="h-8 px-3 text-sm font-normal bg-white border border-gray-200 text-gray-700 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50/50 flex items-center gap-1.5 rounded-md shadow-sm">
                            <HelpCircle className="h-4 w-4" />
                            Contact Tech Support
                        </Button>
                        <ExitButton iconSrc={exitIcon} onExit={handleExit} />
                    </div>
                </div>
            </div>

            <ExitDialog
                isOpen={showExitDialog}
                onClose={() => setShowExitDialog(false)}
                onStillLeave={handleStillLeave}
                onStayToContiune={handleStayToContinue}
            />

            <FeedbackDialog
                isOpen={showFeedbackDialog}
                onClose={() => setShowFeedbackDialog(false)}
                title={FEEDBACK_FORM_TITLES.TECHNICAL_SUPPORT}
            />
        </>
    );
};

export default MockCaseHeader;
